/**
 * Menu "Apri chat con": scelta del numero aziendale quando l'utente ne vede
 * piu' d'uno. Ancorato al pulsante premuto, in un portale sopra tutto; si
 * chiude con Escape e col click fuori (lo sfondo intercetta anche la rotella,
 * quindi la pagina sotto non scorre finche' il menu e' aperto).
 */
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MessageCircle } from 'lucide-react';
import type { WhatsAppSessionConfig } from '../../lib/hooks/useWhatsAppConfigs';
import { etichettaNumero } from '../../lib/services/whatsapp/phoneDigits';

export interface ChooserPos {
  digits: string;
  name: string | null;
  left: number;
  top?: number;
  bottom?: number;
}

export function WhatsAppConfigChooser({
  pos,
  configs,
  onPick,
  onClose,
}: {
  pos: ChooserPos;
  configs: WhatsAppSessionConfig[];
  onPick: (cfg: WhatsAppSessionConfig) => void;
  onClose: () => void;
}) {
  const primaVoce = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    primaVoce.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div
        role="menu"
        aria-label="Apri chat con"
        className="fixed z-[70] w-64 max-h-[60vh] overflow-y-auto rounded-lg border border-[rgba(55,53,47,0.09)] bg-white py-1 shadow-lg dark:border-white/10 dark:bg-[#252528]"
        style={{
          left: `${pos.left}px`,
          ...(pos.top !== undefined ? { top: `${pos.top}px` } : {}),
          ...(pos.bottom !== undefined ? { bottom: `${pos.bottom}px` } : {}),
        }}
      >
        <div className="px-3 pb-1 pt-1.5 text-[10.5px] font-medium uppercase tracking-wide text-[#787774] dark:text-gray-500">
          Apri chat con
        </div>
        {configs.map((cfg, i) => {
          const numero = etichettaNumero(cfg.phoneNumber);
          return (
            <button
              key={cfg.id}
              ref={i === 0 ? primaVoce : undefined}
              type="button"
              role="menuitem"
              onClick={() => onPick(cfg)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] text-[#37352F] hover:bg-[rgba(55,53,47,0.06)] focus:bg-[rgba(55,53,47,0.06)] focus:outline-none dark:text-white dark:hover:bg-white/[0.06] dark:focus:bg-white/[0.06]"
            >
              <MessageCircle className="h-3.5 w-3.5 flex-shrink-0 text-[#1FA855] dark:text-[#4ADE80]" strokeWidth={1.5} />
              <span className="min-w-0 flex-1 truncate">{cfg.displayName || numero || 'WhatsApp'}</span>
              {cfg.displayName && numero && (
                <span className="flex-shrink-0 text-[10.5px] text-[#787774] dark:text-gray-500">{numero}</span>
              )}
            </button>
          );
        })}
      </div>
    </>,
    document.body
  );
}
